import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type SponsorType = 'SINGLE_STORE' | 'SHOPPING_MALL';

export interface SponsorBusinessEntityDto {
  id: string;
  partitaIva: string;
  businessName: string;
  storeIdentifier: string | null;
  aliases: string[];
  isActive: boolean;
}

export interface SponsorDto {
  id: string;
  name: string;
  type: SponsorType;
  logoUrl: string | null;
  address: string | null;
  isActive: boolean;
  participatesInLoyalty: boolean;
  businessEntities: SponsorBusinessEntityDto[];
}

export interface AddEntityInput {
  partitaIva: string;
  businessName: string;
  storeIdentifier?: string;
  aliases?: string[];
}

export interface CreateSponsorInput {
  name: string;
  type: SponsorType;
  logoUrl?: string;
  address?: string;
  isActive?: boolean;
  participatesInLoyalty?: boolean;
  businessEntities?: AddEntityInput[];
}

const QK = {
  list: ['sponsors'] as const,
  one: (id: string) => ['sponsors', id] as const,
};

export function useSponsors() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiClient.get<{
        data: SponsorDto[];
        meta: { total: number; page: number; limit: number; totalPages: number };
      }>('/sponsors');
      return res.data;
    },
  });
}

export function useSponsor(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const res = await apiClient.get<SponsorDto>(`/sponsors/${id}`);
      return res.data;
    },
  });
}

export function useCreateSponsor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSponsorInput) => {
      const res = await apiClient.post<SponsorDto>('/sponsors', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsors'] }),
  });
}

export function useUpdateSponsor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: Partial<Omit<CreateSponsorInput, 'businessEntities'>>;
    }) => {
      const res = await apiClient.patch<SponsorDto>(`/sponsors/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsors'] }),
  });
}

export function useDeleteSponsor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/sponsors/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsors'] }),
  });
}

export function useAddEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sponsorId, input }: { sponsorId: string; input: AddEntityInput }) => {
      const res = await apiClient.post<SponsorBusinessEntityDto>(
        `/sponsors/${sponsorId}/entities`,
        input,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsors'] }),
  });
}

export function useRemoveEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sponsorId, entityId }: { sponsorId: string; entityId: string }) => {
      await apiClient.delete(`/sponsors/${sponsorId}/entities/${entityId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsors'] }),
  });
}
